import { axiosClient } from "../api/axiosClient";

export interface NotificationResponse {
  id: string;
  userId?: string;
  mediumId?: string;
  terreiroId?: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  actionRoute?: string;
  avatar?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface NotificationApiResponse {
  id: string;
  userId?: string;
  mediumId?: string;
  terreiroId?: string;
  notificationType: string;
  title: string;
  message: string;
  actionRoute?: string | null;
  avatar?: string | null;
  metadata?: Record<string, unknown> | null;
  readAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface NotificationStatsResponse {
  total: number;
  unread: number;
  today: number;
  thisWeek: number;
  byType: Record<string, number>;
}

export interface NotificationListResponse {
  notifications: NotificationResponse[];
  stats: NotificationStatsResponse;
  nextCursor: string | null;
  hasMore: boolean;
}

interface NotificationListApiResponse {
  notifications: NotificationApiResponse[];
  stats: NotificationStatsResponse;
  nextCursor: string | null;
  hasMore: boolean;
}

const STATUS_LABELS: Record<string, string> = {
  PENDING: "Pendente",
  CONFIRMED: "Confirmada",
  IN_PROGRESS: "Em andamento",
  FINALIZED: "Finalizada",
  REALIZED: "Realizada",
  NOT_REALIZED: "Nao realizada",
  CANCELLED: "Cancelada",
  MODEL: "Modelo",
};

const getStatusLabel = (status?: string): string => {
  if (!status) return "Desconhecido";
  return STATUS_LABELS[status] || status;
};

const legacyStatusRegex = /Status alterado para\s+([A-Z_]+)/i;

const normalizeActivityStatusNotification = (
  notification: NotificationApiResponse,
): Pick<NotificationResponse, "title" | "message"> => {
  const metadata =
    (notification.metadata as
      | { title?: string; oldStatus?: string; newStatus?: string }
      | null
      | undefined) ?? undefined;

  const activityTitle = metadata?.title;
  const oldStatus = metadata?.oldStatus;
  const newStatusFromMetadata = metadata?.newStatus;
  const newStatusFromMessage = notification.message.match(legacyStatusRegex)?.[1];
  const newStatus = newStatusFromMetadata || newStatusFromMessage;

  const title =
    activityTitle && activityTitle.trim().length > 0
      ? `Atividade atualizada: ${activityTitle}`
      : notification.title && notification.title !== "ACTIVITY_STATUS_CHANGED"
        ? notification.title
        : "Atividade atualizada";

  const message =
    oldStatus && newStatus
      ? `Status alterado de ${getStatusLabel(oldStatus)} para ${getStatusLabel(newStatus)}.`
      : newStatus
        ? `Status alterado para ${getStatusLabel(newStatus)}.`
        : notification.message;

  return { title, message };
};

const mapNotification = (n: NotificationApiResponse): NotificationResponse => {
  const normalizedActivity =
    n.notificationType === "ACTIVITY_STATUS_CHANGED"
      ? normalizeActivityStatusNotification(n)
      : null;

  return {
    id: n.id,
    userId: n.userId,
    mediumId: n.mediumId,
    terreiroId: n.terreiroId,
    type: n.notificationType,
    title: normalizedActivity?.title ?? n.title,
    message: normalizedActivity?.message ?? n.message,
    read: !!n.readAt,
    actionRoute: n.actionRoute || undefined,
    avatar: n.avatar || undefined,
    metadata: n.metadata || undefined,
    createdAt: n.createdAt,
    updatedAt: n.updatedAt,
    deletedAt: n.deletedAt,
  };
};

export interface NotificationOwnerScopeParams {
  terreiroId?: string;
  mediumId?: string;
}

const appendOwnerScope = (
  queryParams: URLSearchParams,
  scope?: NotificationOwnerScopeParams,
) => {
  if (scope?.terreiroId) queryParams.append("terreiroId", scope.terreiroId);
  if (scope?.mediumId) queryParams.append("mediumId", scope.mediumId);
};

export const notificationRepository = {
  /**
   * Get user notifications with optional filters
   */
  async getNotifications(params?: {
    type?: string;
    scope?: string;
    read?: "all" | "read" | "unread";
    limit?: number;
    cursor?: string;
    terreiroId?: string;
    mediumId?: string;
  }): Promise<NotificationListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append("type", params.type);
    if (params?.scope) queryParams.append("scope", params.scope);
    if (params?.read) queryParams.append("read", params.read);
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.cursor) queryParams.append("cursor", params.cursor);
    appendOwnerScope(queryParams, params);

    const response = await axiosClient.get<NotificationListApiResponse>(
      `/notifications?${queryParams.toString()}`,
      { withCredentials: true }
    );

    return {
      notifications: response.data.notifications.map(mapNotification),
      stats: response.data.stats,
      nextCursor: response.data.nextCursor,
      hasMore: response.data.hasMore,
    };
  },

  /**
   * Mark a single notification as read
   */
  async markAsRead(id: string): Promise<NotificationResponse> {
    const response = await axiosClient.patch<NotificationApiResponse>(
      `/notifications/${id}/read`,
      {},
      { withCredentials: true }
    );
    return mapNotification(response.data);
  },

  /**
   * Set explicit read status for a notification
   */
  async updateReadStatus(
    id: string,
    read: boolean
  ): Promise<NotificationResponse> {
    const response = await axiosClient.patch<NotificationApiResponse>(
      `/notifications/${id}/read-status`,
      { read },
      { withCredentials: true }
    );

    return mapNotification(response.data);
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(
    scope?: NotificationOwnerScopeParams,
  ): Promise<{ updated: number }> {
    const queryParams = new URLSearchParams();
    appendOwnerScope(queryParams, scope);
    const suffix = queryParams.toString() ? `?${queryParams.toString()}` : "";
    const response = await axiosClient.patch<{ updated: number }>(
      `/notifications/read-all${suffix}`,
      {},
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * Delete a notification
   */
  async deleteNotification(id: string): Promise<void> {
    await axiosClient.delete(`/notifications/${id}`, {
      withCredentials: true,
    });
  },

  /**
   * Delete all notifications for the current user
   */
  async clearAll(
    scope?: NotificationOwnerScopeParams,
  ): Promise<{ deleted: number }> {
    const queryParams = new URLSearchParams();
    appendOwnerScope(queryParams, scope);
    const suffix = queryParams.toString() ? `?${queryParams.toString()}` : "";
    const response = await axiosClient.delete<{ deleted: number }>(
      `/notifications/all${suffix}`,
      { withCredentials: true }
    );
    return response.data;
  },
};
